/**
 * Layout of the printed map sheet in world units. The playable board sits inside a neatline
 * (double rule with graduated ticks); around it is a paper margin with the title cartouche and
 * scale bar, and the whole sheet lies on the dark war-room table.
 */
import type { HexGrid } from '@friction/engine';

export interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface SheetLayout {
  /** Playable board (hex centres + half a hex). */
  board: Rect;
  /** Inner neatline: terrain is drawn inside it. */
  neat: Rect;
  /** Outer rule of the graduated border. */
  frame: Rect;
  /** Paper edge. */
  sheet: Rect;
  /** Area the terrain mesh covers (sheet + drop shadow on the table). */
  mesh: Rect;
}

export const NEAT_PAD = 20;
export const FRAME_W = 11;
const MARGIN = { left: 72, right: 72, top: 150, bottom: 96 };
const SHADOW = 90;

const grow = (r: Rect, l: number, t: number, rr = l, b = t): Rect => ({
  x0: r.x0 - l,
  y0: r.y0 - t,
  x1: r.x1 + rr,
  y1: r.y1 + b,
});

export function sheetLayout(grid: HexGrid): SheetLayout {
  const board: Rect = { x0: 0, y0: 0, x1: grid.worldWidth, y1: grid.worldHeight };
  const neat = grow(board, NEAT_PAD, NEAT_PAD);
  const frame = grow(neat, FRAME_W, FRAME_W);
  const sheet = grow(frame, MARGIN.left, MARGIN.top, MARGIN.right, MARGIN.bottom);
  const mesh = grow(sheet, SHADOW, SHADOW);
  return { board, neat, frame, sheet, mesh };
}
